/**
 * `<oj-*>` Custom Element registry — the runtime code that turns the
 * HTML compiler's `<oj-pay>`, `<oj-link-account-button>`, etc. tags
 * into rendered widgets in the iframe preview AND on the published
 * customer page.
 *
 * Why Custom Elements: the same string of HTML emitted by `toHtml.ts`
 * needs to render two ways — preview-time in the editor iframe (where
 * widgets are inert previews), and runtime on the published page
 * (where they wire into the Customer Action Catalog and actually fire
 * checkout / link-account / redemption flows). Custom Elements give
 * us the upgrade hook: paint a labeled placeholder on
 * `connectedCallback`, then on the published page swap in real
 * behavior via the SDK runtime that loads alongside this script.
 *
 * v1 scope (Phase 2 — this PR):
 *   - Define every `oj-*` element with a labeled placeholder render.
 *   - Read prop-bearing `data-oj-*` attributes and surface them in the
 *     placeholder label so the editor preview matches the React
 *     `SdkPlaceholder` exactly.
 *   - Wire the `data-oj-action` click delegate so Buttons (and any
 *     other element carrying a JSON action descriptor) report clicks
 *     up to a single dispatch — in the editor iframe that dispatch is
 *     a no-op (preview clicks are intercepted by the iframe runtime
 *     and reported as `select` to the parent).
 *
 * Not in scope yet (Phase 3):
 *   - Real SDK integration. The Customer Action Catalog runtime that
 *     fires checkout / link-account flows is a separate ship.
 *   - Slotted children. v1 widgets are leaves with no nested content.
 *   - Custom styling beyond the placeholder. The customer's brand
 *     tokens (`--oj-color-*`) already cascade in from the page root,
 *     so the placeholder picks them up automatically.
 *
 * Idempotent: calling `registerOjElements()` twice (e.g. HMR) is a
 * no-op for already-registered tags. Custom Elements throw on
 * re-registration; we catch and ignore.
 */

// ─────────────────────────────────────────────────────────────────────────
// Element definitions
// ─────────────────────────────────────────────────────────────────────────

/** Tag → label-builder map. Mirrors `renderers/sdkComponents.tsx
 *  labelFor` exactly so the iframe preview matches the React preview. */
const TAG_LABELS: Record<string, (data: DOMStringMap) => string> = {
  'oj-link-account-button': (d) => {
    const partnershipId = d.ojPartnershipId ?? '';
    const oauthProfileId = d.ojOauthProfileId ?? '';
    const buttonText = d.ojText || 'Connect account';
    if (!partnershipId) return '⚠ Link Account: pick a partnership';
    if (!oauthProfileId) return '⚠ Link Account: pick an OAuth profile';
    return `🔗 ${buttonText}`;
  },
  'oj-pay': (d) => {
    const amount = d.ojAmount ?? '';
    return amount ? `💳 Pay ${amount}` : '⚠ Pay: amount required';
  },
  'oj-redeem': () => '🎁 Redeem catalog',
  'oj-rewards-grid': () => '🏆 Rewards grid',
  'oj-account': () => '👤 Account widget',
  'oj-exchange': () => '🔁 Currency exchange',
};

// ─────────────────────────────────────────────────────────────────────────
// Base class
// ─────────────────────────────────────────────────────────────────────────

/**
 * Shared base. Each registered tag gets its own subclass that pins
 * the static label generator — Custom Elements can't share a single
 * constructor across multiple tag names without a wrapper class per
 * tag.
 */
abstract class OjElementBase extends HTMLElement {
  /** Whether the placeholder DOM has been rendered yet. Avoids
   *  double-paint on attribute changes that fire before
   *  `connectedCallback`. */
  private painted = false;

  abstract getLabel(): string;

  connectedCallback() {
    if (this.painted) return;
    this.paintPlaceholder();
    this.painted = true;
  }

  /** Re-render when any `data-oj-*` attribute changes. Custom Elements
   *  observedAttributes is static; we re-read the whole dataset on
   *  any change rather than trying to map attribute names to specific
   *  field reads. Pages don't typically change widget props after
   *  load, so this fires rarely. */
  attributeChangedCallback() {
    if (!this.painted) return;
    this.updatePlaceholder();
  }

  static get observedAttributes(): string[] {
    // Watch the full set of data-oj-* attrs the compiler emits. The
    // browser fires `attributeChangedCallback` whenever any of these
    // changes, regardless of how observed they are.
    return [
      'data-oj-id',
      'data-oj-partnership-id',
      'data-oj-oauth-profile-id',
      'data-oj-text',
      'data-oj-amount',
    ];
  }

  private paintPlaceholder() {
    const isConfigured = !this.getLabel().startsWith('⚠');
    const styles: Record<string, string> = {
      display: 'inline-flex',
      'align-items': 'center',
      'justify-content': 'center',
      'min-width': '200px',
      'min-height': '48px',
      padding: '12px 24px',
      border: `2px dashed ${isConfigured ? 'var(--oj-color-primary, #3b82f6)' : 'var(--oj-color-warning, #f59e0b)'}`,
      'border-radius': '8px',
      background: isConfigured
        ? 'var(--oj-color-surface-1, #f9fafb)'
        : 'var(--oj-color-warning-bg, #fef3c7)',
      color: isConfigured
        ? 'var(--oj-color-text, #111827)'
        : 'var(--oj-color-warning-text, #92400e)',
      'font-size': '14px',
      'font-weight': '500',
      'font-family': 'system-ui, sans-serif',
    };
    // Apply styles directly to the element so the cascade still
    // works (the parent's `--oj-color-*` variables resolve here).
    for (const [k, v] of Object.entries(styles)) {
      this.style.setProperty(k, v);
    }
    this.textContent = this.getLabel();
  }

  private updatePlaceholder() {
    this.textContent = this.getLabel();
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Concrete subclasses (one per tag — Custom Elements requires this)
// ─────────────────────────────────────────────────────────────────────────

class OjLinkAccountButton extends OjElementBase {
  getLabel() {
    return TAG_LABELS['oj-link-account-button'](this.dataset);
  }
}

class OjPay extends OjElementBase {
  getLabel() {
    return TAG_LABELS['oj-pay'](this.dataset);
  }
}

class OjRedeem extends OjElementBase {
  getLabel() {
    return TAG_LABELS['oj-redeem'](this.dataset);
  }
}

class OjRewardsGrid extends OjElementBase {
  getLabel() {
    return TAG_LABELS['oj-rewards-grid'](this.dataset);
  }
}

class OjAccount extends OjElementBase {
  getLabel() {
    return TAG_LABELS['oj-account'](this.dataset);
  }
}

class OjExchange extends OjElementBase {
  getLabel() {
    return TAG_LABELS['oj-exchange'](this.dataset);
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Form primitives — differ from the placeholder-based widgets above
// because they paint REAL interactive inputs at runtime.
// ─────────────────────────────────────────────────────────────────────────

/**
 * `<oj-form-input>` — paints a labeled input/textarea inside the
 * Custom Element. Reads its config from `data-oj-*` attrs:
 *
 *   - `data-oj-name` — field key for the submission payload
 *   - `data-oj-type` — text / email / tel / number / textarea
 *   - `data-oj-label` — display label (optional)
 *   - `data-oj-placeholder` — placeholder text (optional)
 *   - `data-oj-required` — "true" to mark required
 *
 * The input is plain HTML; styling cascades from the parent's
 * `--oj-*` brand tokens (no encapsulating shadow DOM by design — the
 * customer's brand should reach through and style the form).
 */
class OjFormInput extends HTMLElement {
  private painted = false;

  static get observedAttributes(): string[] {
    return [
      'data-oj-name',
      'data-oj-type',
      'data-oj-label',
      'data-oj-placeholder',
      'data-oj-required',
    ];
  }

  connectedCallback() {
    if (this.painted) return;
    this.paint();
    this.painted = true;
  }

  attributeChangedCallback() {
    if (this.painted) this.paint();
  }

  private paint() {
    const name = this.dataset.ojName ?? '';
    const type = this.dataset.ojType ?? 'text';
    const label = this.dataset.ojLabel ?? '';
    const placeholder = this.dataset.ojPlaceholder ?? '';
    const required = this.dataset.ojRequired === 'true';

    const inputId = `oj-form-input-${Math.random().toString(36).slice(2, 10)}`;
    const inputAttrs =
      ` id="${inputId}" name="${escapeAttr(name)}"` +
      ` placeholder="${escapeAttr(placeholder)}"` +
      (required ? ' required' : '') +
      // Inputs inherit fonts/colors from the brand cascade. We give
      // them sane defaults that work whether or not the customer has
      // touched the wizard.
      ' style="display:block;width:100%;padding:10px 12px;font-family:var(--oj-font-body, system-ui, sans-serif);font-size:14px;color:var(--oj-color-text, #111827);background:var(--oj-color-background, #fff);border:1px solid var(--oj-color-border, #e5e7eb);border-radius:var(--oj-radius-md, 8px);box-sizing:border-box;"';

    const inputEl =
      type === 'textarea'
        ? `<textarea${inputAttrs} rows="4"></textarea>`
        : `<input type="${escapeAttr(type)}"${inputAttrs}/>`;

    const requiredMarker = required
      ? ' <span style="color:var(--oj-color-error, #ef4444)">*</span>'
      : '';
    this.innerHTML =
      (label
        ? `<label for="${inputId}" style="display:block;margin-bottom:6px;font-family:var(--oj-font-body, system-ui, sans-serif);font-size:13px;color:var(--oj-color-text, #111827);">${escapeText(label)}${requiredMarker}</label>`
        : '') + inputEl;
    this.style.display = 'block';
    this.style.width = '100%';
  }
}

/**
 * `<oj-form-submit>` — submit button. On click, walks ancestors to
 * find the closest containing section/stack, collects the values of
 * every `<oj-form-input>` underneath it, and POSTs to
 * `/api/landing-pages/form-submissions`. The endpoint is public; no
 * auth required.
 *
 * After a successful submission the button replaces itself with the
 * configured success message. Failure shows an inline error and lets
 * the user retry.
 */
class OjFormSubmit extends HTMLElement {
  private painted = false;
  private button: HTMLButtonElement | null = null;
  private status: HTMLDivElement | null = null;

  static get observedAttributes(): string[] {
    return ['data-oj-text', 'data-oj-form-type', 'data-oj-success-message'];
  }

  connectedCallback() {
    if (this.painted) return;
    this.paint();
    this.painted = true;
  }

  attributeChangedCallback() {
    if (this.painted) this.paint();
  }

  private paint() {
    const text = this.dataset.ojText ?? 'Submit';
    this.innerHTML = '';
    this.style.display = 'block';

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.textContent = text;
    this.button.style.cssText =
      'display:inline-flex;align-items:center;justify-content:center;padding:12px 24px;font-family:var(--oj-font-body, system-ui, sans-serif);font-size:14px;font-weight:600;color:var(--oj-color-on-primary, #fff);background:var(--oj-color-primary, #4f46e5);border:none;border-radius:var(--oj-radius-md, 8px);cursor:pointer;';
    this.button.addEventListener('click', () => this.submit());

    this.status = document.createElement('div');
    this.status.style.cssText =
      'margin-top:8px;font-family:var(--oj-font-body, system-ui, sans-serif);font-size:13px;';
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');

    this.appendChild(this.button);
    this.appendChild(this.status);
  }

  private async submit() {
    if (!this.button || !this.status) return;
    const fields = this.collectFields();
    const formType = this.dataset.ojFormType ?? 'contact';
    const successMessage =
      this.dataset.ojSuccessMessage ?? "Thanks — we'll be in touch.";

    // Disable the button while in flight so a fast double-click can't
    // double-submit.
    const originalLabel = this.button.textContent;
    this.button.disabled = true;
    this.button.textContent = 'Sending…';
    this.status.textContent = '';
    this.status.style.color = '';

    try {
      const response = await fetch('/api/landing-pages/form-submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields,
          formType,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as
          | { error?: string }
          | null;
        throw new Error(body?.error ?? `Submit failed (${response.status})`);
      }
      // Replace the button with a success message.
      this.button.style.display = 'none';
      this.status.textContent = successMessage;
      this.status.style.color = 'var(--oj-color-text, #111827)';
    } catch (err) {
      this.button.disabled = false;
      this.button.textContent = originalLabel ?? 'Submit';
      this.status.textContent =
        err instanceof Error ? err.message : 'Submit failed';
      this.status.style.color = 'var(--oj-color-error, #ef4444)';
    }
  }

  /** Walk up to the closest section/stack ancestor and collect every
   *  `<oj-form-input>` under it. The closest-ancestor heuristic is
   *  good enough for v1 — most pages have at most one form. If a
   *  page ever grows two side-by-side forms, the customer can put
   *  each in its own Section and the closest-ancestor walk picks the
   *  right one. */
  private collectFields(): Record<string, string> {
    let scope: HTMLElement | null = this.parentElement;
    while (scope && scope.tagName.toLowerCase() !== 'section') {
      // The form-input collection works regardless of layout
      // primitive; we walk up until we hit a section, the data-oj-root,
      // or the document body.
      if (scope.dataset.ojRoot === 'true') break;
      scope = scope.parentElement;
    }
    if (!scope) scope = document.body;
    const inputs = scope.querySelectorAll('oj-form-input');
    const fields: Record<string, string> = {};
    for (const inputHost of Array.from(inputs)) {
      const inner = inputHost.querySelector(
        'input, textarea',
      ) as HTMLInputElement | HTMLTextAreaElement | null;
      if (!inner) continue;
      const name = inner.getAttribute('name') ?? '';
      if (!name) continue;
      fields[name] = inner.value;
    }
    return fields;
  }
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// ─────────────────────────────────────────────────────────────────────────
// Registration
// ─────────────────────────────────────────────────────────────────────────

interface RegistrarOptions {
  /** Custom-element registry — defaults to the global one. Tests pass
   *  a fake to avoid polluting real registrations across runs. */
  registry?: CustomElementRegistry;
}

/**
 * Register every `<oj-*>` tag. Idempotent — already-registered tags
 * are skipped without throwing. Returns the list of tags that were
 * newly registered (useful for tests + diagnostics).
 */
export function registerOjElements(opts: RegistrarOptions = {}): string[] {
  const registry =
    opts.registry ??
    (typeof customElements !== 'undefined' ? customElements : undefined);
  if (!registry) return [];

  const definitions: Array<[string, CustomElementConstructor]> = [
    ['oj-link-account-button', OjLinkAccountButton],
    ['oj-pay', OjPay],
    ['oj-redeem', OjRedeem],
    ['oj-rewards-grid', OjRewardsGrid],
    ['oj-account', OjAccount],
    ['oj-exchange', OjExchange],
    ['oj-form-input', OjFormInput],
    ['oj-form-submit', OjFormSubmit],
  ];
  const newlyRegistered: string[] = [];
  for (const [tag, ctor] of definitions) {
    if (registry.get(tag)) continue;
    try {
      registry.define(tag, ctor);
      newlyRegistered.push(tag);
    } catch {
      // Re-registration race or environment without Custom Elements
      // support — quietly skip rather than crash the page.
    }
  }
  return newlyRegistered;
}

// ─────────────────────────────────────────────────────────────────────────
// Action delegate
// ─────────────────────────────────────────────────────────────────────────

/**
 * Single delegated click handler for `[data-oj-action]` elements.
 * The HTML compiler emits a JSON-encoded action descriptor on every
 * Button (and on any future widget that exposes interactive behavior).
 * On the published page, this delegate dispatches into the Customer
 * Action Catalog runtime (Phase 3). In the editor preview iframe,
 * the iframe runtime's own click handler intercepts first (capture
 * phase) and reports `select` to the parent — this delegate never
 * fires there because the parent suppresses default + propagation.
 *
 * Returns a teardown function for tests + HMR. Never called by the
 * iframe runtime, which fires-and-forgets the delegate at boot.
 */
export function attachOjActionDelegate(
  doc: Document,
  onAction: (descriptor: unknown, event: MouseEvent) => void,
): () => void {
  const handler = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const node = target.closest('[data-oj-action]') as HTMLElement | null;
    if (!node) return;
    const raw = node.getAttribute('data-oj-action');
    if (!raw) return;
    let descriptor: unknown;
    try {
      descriptor = JSON.parse(raw);
    } catch {
      // Malformed descriptor — ignore. The HTML compiler always emits
      // valid JSON; reaching here means the customer's source has
      // been tampered with after publish.
      return;
    }
    onAction(descriptor, event);
  };
  doc.addEventListener('click', handler);
  return () => doc.removeEventListener('click', handler);
}
